import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
} from '@chakra-ui/react';
import { Row } from '@tanstack/react-table';
import _ from 'lodash';
import React from 'react';

import {
  BorrowingBaseTestTypeGQLType,
  ReceivableBorrowingBaseTestWaiverGQLType,
  ReceivableBorrowingBaseTestWaiverInputGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

type VerificationBulkDeleteWaiverModalProps = {
  isOpen: boolean;
  onClose: () => void;
  queuedReceivableWaiverUpdates: ReceivableBorrowingBaseTestWaiverInputGQLType[];
  setQueuedReceivableWaiverUpdates: React.Dispatch<
    React.SetStateAction<ReceivableBorrowingBaseTestWaiverInputGQLType[]>
  >;
  queuedReceivableWaiverDeletions: ReceivableBorrowingBaseTestWaiverInputGQLType[];
  setQueuedReceivableWaiverDeletions: React.Dispatch<
    React.SetStateAction<ReceivableBorrowingBaseTestWaiverInputGQLType[]>
  >;
  activeReceivableWaivers: ReceivableBorrowingBaseTestWaiverGQLType[];
  selectedRows: Row<ReceivableBorrowingBaseTestWaiverInputGQLType>[];
  resetRowSelection: () => void;
  numberOfUniqueSelectedWaivers: number;
};
export const VerificationBulkDeleteWaiverModal = ({
  isOpen,
  onClose,
  queuedReceivableWaiverUpdates,
  setQueuedReceivableWaiverUpdates,
  activeReceivableWaivers,
  queuedReceivableWaiverDeletions,
  setQueuedReceivableWaiverDeletions,
  selectedRows,
  resetRowSelection,
  numberOfUniqueSelectedWaivers,
}: VerificationBulkDeleteWaiverModalProps) => {
  const onCloseModal = () => {
    resetRowSelection();
    onClose();
  };

  const onQueueDeletions = () => {
    const newUpdateQueue = [...queuedReceivableWaiverUpdates];
    const newDeletionQueue = [...queuedReceivableWaiverDeletions];

    _.forEach(selectedRows, (row) => {
      const finleyReceivableId: string = row.getValue('finleyReceivableId');
      const borrowingBaseTestType: BorrowingBaseTestTypeGQLType = row.getValue(
        'borrowingBaseTestType',
      );
      const borrowingBaseTestKey: string = row.getValue('borrowingBaseTestKey');

      // check if in queued waivers
      const existingWaiverInUpdateQueueIndex = _.findIndex(
        queuedReceivableWaiverUpdates,
        {
          finleyReceivableId,
          borrowingBaseTestType,
          borrowingBaseTestKey,
        },
      );
      if (existingWaiverInUpdateQueueIndex !== -1) {
        // if waiver to delete exists in the update queue
        // remove from update queue
        newUpdateQueue.splice(existingWaiverInUpdateQueueIndex, 1);
        setQueuedReceivableWaiverUpdates(newUpdateQueue);
      }

      // check if waiver is already in active waivers
      const existingActiveWaiver = _.find(activeReceivableWaivers, {
        finleyReceivableId,
        borrowingBaseTestType,
        borrowingBaseTestKey,
      });
      if (existingActiveWaiver) {
        // if waiver exists in the stored active waivers
        // check if receivable is already in deletion queue
        const existingWaiverInDeletionQueueIndex = _.findIndex(
          queuedReceivableWaiverDeletions,
          {
            finleyReceivableId,
            borrowingBaseTestType,
            borrowingBaseTestKey,
          },
        );
        if (existingWaiverInDeletionQueueIndex === -1) {
          const {
            id: _id,
            created_at: _created_at,
            updated_at: _updated_at,
            __typename: _typename,
            ...existingActiveWaiverInputs
          } = existingActiveWaiver;
          // add to deletion queue if not already in it
          newDeletionQueue.push(existingActiveWaiverInputs);
          setQueuedReceivableWaiverDeletions(newDeletionQueue);
        }
      }
    });
    setQueuedReceivableWaiverUpdates(newUpdateQueue);
    setQueuedReceivableWaiverDeletions(newDeletionQueue);
    onCloseModal();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onCloseModal} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Selected Waivers</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb="24px">
              {`Are you sure you want to queue ${numberOfUniqueSelectedWaivers} selected waivers for deletion?`}
            </Text>
            <Text mb="24px">
              You will need to click &quot;Save&quot; in the Waiver Queue to
              save these changes.
            </Text>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="aurora"
              variant="outline"
              borderWidth="2px"
              mr="32px"
              onClick={onCloseModal}
            >
              Cancel
            </Button>
            <Button colorScheme="aurora" onClick={onQueueDeletions}>
              Queue Deletions
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
